import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  SecurityContext,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';

/** Link button size variants */
export type LinkButtonSize = 'sm' | 'md' | 'lg';

/** Link target window/tab options */
export type LinkButtonTarget = 'same-tab' | 'new-tab' | 'new-window';

/** Text alignment options within the button */
export type LinkButtonTextAlign = 'left' | 'center' | 'right';

/**
 * HxLinkButton - Outline-styled anchor button component for navigation
 *
 * @remarks
 *   - Sanitizes href to prevent XSS attacks
 *   - Adds `rel="noopener noreferrer"` when `target="_blank"` for security
 *   - Supports WCAG 2.1 AA accessibility (keyboard navigation, ARIA labels, color contrast)
 *   - Uses design system tokens for all styling
 *
 * @example
 *   ```html
 *   <hx-link-button label="Documentation" href="/docs" size="md" />
 *   ```;
 *
 * @see {@link https://docs.example.com/components/link-button | Documentation}
 */
@Component({
  selector: 'hx-link-button',
  standalone: true,
  imports: [NgClass, HxIcon],
  templateUrl: './link-button.html',
  styleUrls: ['./link-button-tokens.css', './link-button.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class LinkButton extends HxBaseControl {
  /** @internal */
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Button text content (optional if using content projection)
   *
   * @example
   *   label = 'Help Documentation';
   */
  readonly label = input<string>('Link');

  /**
   * Link destination URL Supports: http://, https://, mailto:, tel:, relative paths (/)
   *
   * @example
   *   href = 'https://example.com/help';
   */
  readonly href = input<string>('');

  /**
   * Link target window/tab
   *
   * @example
   *   target = 'new-tab';
   *
   * @default 'new-window'
   */
  readonly target = input<LinkButtonTarget>('new-window');

  /**
   * Button size variant
   *
   * @example
   *   size = 'md';
   *
   * @default 'sm'
   */
  readonly size = input<LinkButtonSize>('sm');

  /**
   * Display external link icon on the right
   *
   * @example
   *   showIcon={false}
   *
   * @default true
   */
  readonly showIcon = input<boolean>(true);

  /**
   * Text alignment within button
   *
   * @example
   *   textAlign = 'left';
   *
   * @default 'center'
   */
  readonly textAlign = input<LinkButtonTextAlign>('center');

  /**
   * /** Custom ARIA label (overrides label prop for accessibility)
   *
   * @example
   *   ariaLabel = 'Learn more about accessibility features';
   */
  override readonly ariaLabel = input<string | undefined>();

  /**
   * Make button take full width of parent container
   *
   * @example
   *   fillSpace={true}
   *
   * @default false
   */
  readonly fillSpace = input<boolean>(false);

  /**
   * Sanitized href value (XSS-safe)
   *
   * @internal
   */
  protected readonly sanitizedHref = computed(() => {
    const rawHref = this.href();
    if (!rawHref || rawHref.trim() === '') return null;

    // Protocol allowlist: http://, https://, mailto:, tel:, /, #
    const allowedProtocols = /^(https?:\/\/|mailto:|tel:|\/|#)/i;
    if (!allowedProtocols.test(rawHref)) {
      return null;
    }

    return this.sanitizer.sanitize(SecurityContext.URL, rawHref) || null;
  });

  /**
   * Effective ARIA label (priority: ariaLabel > label > "Link")
   *
   * @internal
   */
  protected readonly effectiveAriaLabel = computed(() => {
    return this.ariaLabel() || this.label() || 'Link';
  });

  /**
   * Maps semantic target values to HTML target attributes
   *
   * @internal
   */
  protected readonly htmlTarget = computed(() => {
    const targetMap = {
      'same-tab': '_self',
      'new-tab': '_blank',
      'new-window': '_self', // We'll handle this with click event
    };
    return targetMap[this.target()] || '_self';
  });

  /**
   * CSS class map for [ngClass] binding
   *
   * @internal
   */
  protected readonly buttonClasses = computed(() => ({
    [`hx-link-button--${this.size()}`]: true,
    [`hx-link-button--align-${this.textAlign()}`]: true,
    'hx-link-button--disabled': this.disabled(),
    'hx-link-button--full-width': this.fillSpace(),
  }));

  /**
   * Security attributes for external links (rel="noopener noreferrer" when target="new-tab" or "new-window")
   *
   * @internal
   */
  protected readonly relAttribute = computed(() => {
    return this.target() === 'new-tab' || this.target() === 'new-window' ? 'noopener noreferrer' : null;
  });

  /**
   * Handle click events for new-window behavior
   *
   * @internal
   */
  protected onClick(event: MouseEvent): void {
    if (this.target() === 'new-window') {
      const href = this.sanitizedHref();
      if (href && !this.disabledState()) {
        event.preventDefault();
        window.open(href, '_blank', 'scrollbars=yes,resizable=yes,menubar=yes,toolbar=yes');
      }
    }
  }
}
